'use client'

import styles from './AppLink.module.css'
import { Link } from '@/i18n/navigation'
import { AnchorHTMLAttributes, ButtonHTMLAttributes, PropsWithChildren } from "react"

import { AppLinkSize } from './AppLink.types'
export { AppLinkSize }

type AppLinkBaseProps = {
  size?: AppLinkSize
  internal?: boolean
  highlight?: boolean
  disabled?: boolean
  className?: string,
  target?: string,
  alt?: string,
}

type AnchorPropsSafe = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'type' | 'disabled' | 'href' | 'rel'>

type AppLinkAsLinkProps =
  AppLinkBaseProps & {
    href: string
  } & AnchorPropsSafe

type AppLinkAsButtonProps =
  AppLinkBaseProps & {
    href?: never
    type?: 'button' | 'submit' | 'reset'
  } & ButtonHTMLAttributes<HTMLButtonElement>

type AppLinkProps = AppLinkAsLinkProps | AppLinkAsButtonProps

function isLinkProps(p: AppLinkProps): p is AppLinkAsLinkProps {
  return typeof (p as any).href === 'string'
}

export default function AppLink (props: PropsWithChildren<AppLinkProps>) {
  const {
    size = AppLinkSize.M,
    children,
    className,
    internal,
    highlight = false,
    disabled = false,
    target = '',
    alt = '',
    ...passthrough
  } = props as PropsWithChildren<AppLinkProps> & Record<string, unknown>

  console.log(props)

  const classes = `${styles.main} ${highlight ? styles.highlight : ''} ${disabled ? styles.disabled : ''} ${size ?? ''} ${className ?? ''}`

  // render link
  if (isLinkProps(props)) {
    const { href, onClick, tabIndex, ...rest } = passthrough as AnchorPropsSafe & { href: string }

    const isInternal = internal ?? href.startsWith("/")

    const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
      if (disabled) e.preventDefault()
      onClick?.(e)
    }
    if (isInternal) {
      return (
        <Link
          href={href}
          className={classes}
          aria-disabled={disabled || undefined}
          tabIndex={disabled ? -1 : tabIndex}
          onClick={handleClick}
          title={alt}
        >
          {children}
        </Link>
      )
    }

    return (
      <a 
        href={href}
        target={target}
        rel='noopener noreferrer'
        className={classes}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : tabIndex}
        onClick={handleClick}
        title={alt}
        {...rest}
      >
        {children}
      </a>
    )
  }

  // render button
  const { type = 'button', ...rest } = passthrough as ButtonHTMLAttributes<HTMLButtonElement> & {
    type?: 'button' | 'submit' | 'reset'
  }

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled}
      {...rest}
    >
      {children}
    </button>
  ) 
}